/**
 * @see https://umijs.org/docs/max/access#access
 *
 * ⚠️ 安全警告：
 * 此文件仅用于前端UI显示控制，不能作为安全屏障！
 * 真正的权限验证必须在后端进行。
 *
 * 前端权限控制的作用：
 * 1. 改善用户体验（隐藏无权限的功能）
 * 2. 减少无效请求
 * 3. 提供友好的界面提示
 *
 * 安全原则：
 * 1. 前端权限控制可以被绕过
 * 2. 后端必须验证每个请求的权限
 * 3. 敏感操作必须双重验证
 */
import { hasMenuPermission } from '@/utils/auth';
import { MENU_PERMISSIONS, SUB_MENU_PERMISSIONS } from '@/constants/permissions';

export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const dashboardChildPermissions = [
    MENU_PERMISSIONS.DASHBOARD,
    SUB_MENU_PERMISSIONS.DASHBOARD_OPERATION_DESK,
    SUB_MENU_PERMISSIONS.DASHBOARD_COMPREHENSIVE,
  ];
  const contentChildPermissions = [
    MENU_PERMISSIONS.CONTENT,
    SUB_MENU_PERMISSIONS.CONTENT_ABNORMAL,
    SUB_MENU_PERMISSIONS.CONTENT_ALL,
    SUB_MENU_PERMISSIONS.CONTENT_CONTENT_ANALYSIS,
  ];
  const accountChildPermissions = [
    MENU_PERMISSIONS.ACCOUNT,
    SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL,
    SUB_MENU_PERMISSIONS.ACCOUNT_EDUCATION,
    SUB_MENU_PERMISSIONS.ACCOUNT_MEDIA,
    SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL_ANALYSIS,
  ];

  // 前端权限控制 - 仅用于UI显示，不可信！
  const accessResult = {
    // 主菜单权限检查
    [MENU_PERMISSIONS.DASHBOARD]: hasMenuPermission(dashboardChildPermissions),
    [MENU_PERMISSIONS.STATISTICS]: hasMenuPermission(MENU_PERMISSIONS.STATISTICS),
    [MENU_PERMISSIONS.CONTENT]: hasMenuPermission(contentChildPermissions),
    [MENU_PERMISSIONS.NOTIFICATION]: hasMenuPermission(MENU_PERMISSIONS.NOTIFICATION),
    [MENU_PERMISSIONS.ACCOUNT]: hasMenuPermission(accountChildPermissions),
    [MENU_PERMISSIONS.PLATFORM_SETTINGS]: hasMenuPermission(MENU_PERMISSIONS.PLATFORM_SETTINGS),
    [MENU_PERMISSIONS.PERSONAL]: hasMenuPermission(MENU_PERMISSIONS.PERSONAL),
    [MENU_PERMISSIONS.HELP]: hasMenuPermission(MENU_PERMISSIONS.HELP),
    [MENU_PERMISSIONS.TEST_TOOLS]: hasMenuPermission(MENU_PERMISSIONS.TEST_TOOLS),

    // 子菜单权限检查
    // 操作台子菜单
    [SUB_MENU_PERMISSIONS.DASHBOARD_OPERATION_DESK]: hasMenuPermission(SUB_MENU_PERMISSIONS.DASHBOARD_OPERATION_DESK),

    // 内容管理子菜单
    [SUB_MENU_PERMISSIONS.CONTENT_ABNORMAL]: hasMenuPermission(SUB_MENU_PERMISSIONS.CONTENT_ABNORMAL),
    [SUB_MENU_PERMISSIONS.CONTENT_ALL]: hasMenuPermission(SUB_MENU_PERMISSIONS.CONTENT_ALL),
    [SUB_MENU_PERMISSIONS.DASHBOARD_COMPREHENSIVE]: hasMenuPermission(SUB_MENU_PERMISSIONS.DASHBOARD_COMPREHENSIVE),
    [SUB_MENU_PERMISSIONS.CONTENT_CONTENT_ANALYSIS]: hasMenuPermission(SUB_MENU_PERMISSIONS.CONTENT_CONTENT_ANALYSIS),

    // 通知管理子菜单
    [SUB_MENU_PERMISSIONS.NOTIFICATION_INBOX]: hasMenuPermission(SUB_MENU_PERMISSIONS.NOTIFICATION_INBOX),
    [SUB_MENU_PERMISSIONS.NOTIFICATION_SEND]: hasMenuPermission(SUB_MENU_PERMISSIONS.NOTIFICATION_SEND),
    [SUB_MENU_PERMISSIONS.NOTIFICATION_HISTORY]: hasMenuPermission(SUB_MENU_PERMISSIONS.NOTIFICATION_HISTORY),

    // 账号管理子菜单
    [SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL]: hasMenuPermission(SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL),
    [SUB_MENU_PERMISSIONS.ACCOUNT_EDUCATION]: hasMenuPermission(SUB_MENU_PERMISSIONS.ACCOUNT_EDUCATION),
    [SUB_MENU_PERMISSIONS.ACCOUNT_MEDIA]: hasMenuPermission(SUB_MENU_PERMISSIONS.ACCOUNT_MEDIA),
    [SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL_ANALYSIS]: hasMenuPermission(SUB_MENU_PERMISSIONS.ACCOUNT_SCHOOL_ANALYSIS),

    // 平台设置子菜单
    [SUB_MENU_PERMISSIONS.PLATFORM_ABNORMAL_DATA]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_ABNORMAL_DATA),
    [SUB_MENU_PERMISSIONS.PLATFORM_TAG_LIBRARY]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_TAG_LIBRARY),
    [SUB_MENU_PERMISSIONS.PLATFORM_ROLE_MANAGEMENT]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_ROLE_MANAGEMENT),
    [SUB_MENU_PERMISSIONS.PLATFORM_OPERATION_LOG]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_OPERATION_LOG),
    [SUB_MENU_PERMISSIONS.PLATFORM_SYSTEM_CONFIG]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_SYSTEM_CONFIG),
    [SUB_MENU_PERMISSIONS.PLATFORM_SCHOOL_TAG_MANAGEMENT]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_SCHOOL_TAG_MANAGEMENT),
    [SUB_MENU_PERMISSIONS.PLATFORM_ARTICLE_PULL_CACHE]: hasMenuPermission(SUB_MENU_PERMISSIONS.PLATFORM_ARTICLE_PULL_CACHE),
    [SUB_MENU_PERMISSIONS.HELP_DOCUMENT_MANAGEMENT]: hasMenuPermission(SUB_MENU_PERMISSIONS.HELP_DOCUMENT_MANAGEMENT),

    // 个人中心子菜单
    [SUB_MENU_PERMISSIONS.PERSONAL_USER_INFO]: hasMenuPermission(SUB_MENU_PERMISSIONS.PERSONAL_USER_INFO),
    [SUB_MENU_PERMISSIONS.PERSONAL_SUB_ACCOUNT]: hasMenuPermission(SUB_MENU_PERMISSIONS.PERSONAL_SUB_ACCOUNT) && currentUser?.mainAccount === 1,
    [SUB_MENU_PERMISSIONS.PERSONAL_CHANGE_PASSWORD]: hasMenuPermission(SUB_MENU_PERMISSIONS.PERSONAL_CHANGE_PASSWORD),

    // 帮助子菜单
    [SUB_MENU_PERMISSIONS.HELP_DOCUMENT_VIEW]: hasMenuPermission(SUB_MENU_PERMISSIONS.HELP_DOCUMENT_VIEW),

    // 测试工具子菜单
    [SUB_MENU_PERMISSIONS.TEST_TOOLS_CONTENT_TEST]: hasMenuPermission(SUB_MENU_PERMISSIONS.TEST_TOOLS_CONTENT_TEST),
    [SUB_MENU_PERMISSIONS.TEST_TOOLS_ACCOUNT_RELATION_TEST]: hasMenuPermission(SUB_MENU_PERMISSIONS.TEST_TOOLS_ACCOUNT_RELATION_TEST),
  };

  return accessResult;
}
